import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';

/**
 * Page-level refresh.
 *
 * One Refresh button lives in the portal header. Each screen registers the
 * fetch(es) that load *its own* data with usePageRefresh(); clicking the button
 * re-runs exactly those — the page's list, report or section — instead of
 * reloading the browser tab, which re-requests the session, menus, dropdowns
 * and every other API on the page.
 *
 * Screens can register more than once (a page and a section inside it); a click
 * runs every registered handler. With nothing registered the button is hidden,
 * so it never offers a refresh that would do nothing.
 */

// Registration is split from state so that a handler count changing does not
// hand consumers a new register() and make them re-register in a loop.
const RegisterContext = createContext(null);
const StateContext = createContext({ available: false, refreshing: false, refresh: () => {} });

// Minimum spin, so a fast response still reads as "it refreshed".
const MIN_SPIN_MS = 600;

export const PageRefreshProvider = ({ children }) => {
    const handlersRef = useRef(new Set());
    const [count, setCount] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const register = useCallback((handler) => {
        handlersRef.current.add(handler);
        setCount(handlersRef.current.size);
        return () => {
            handlersRef.current.delete(handler);
            setCount(handlersRef.current.size);
        };
    }, []);

    const refresh = useCallback(async () => {
        if (refreshing || handlersRef.current.size === 0) return;
        setRefreshing(true);
        const started = Date.now();
        // A handler may dispatch (saga-driven, returns an action) or return a
        // promise; either is fine. One failing does not stop the others.
        await Promise.allSettled(
            Array.from(handlersRef.current).map(h => Promise.resolve().then(h))
        );
        const remaining = MIN_SPIN_MS - (Date.now() - started);
        if (remaining > 0) await new Promise(r => setTimeout(r, remaining));
        setRefreshing(false);
    }, [refreshing]);

    const state = useMemo(
        () => ({ available: count > 0, refreshing, refresh }),
        [count, refreshing, refresh]
    );

    return (
        <RegisterContext.Provider value={register}>
            <StateContext.Provider value={state}>{children}</StateContext.Provider>
        </RegisterContext.Provider>
    );
};

/**
 * Register this screen's data fetch with the header Refresh button.
 *
 *   usePageRefresh(() => dispatch(getInvoices(searchParams)));
 *
 * The callback may change every render (new filters, new page) — the latest one
 * is always what runs. Outside a PageRefreshProvider this is a no-op.
 */
export const usePageRefresh = (callback) => {
    const register = useContext(RegisterContext);
    const latest = useRef(callback);
    latest.current = callback;

    useEffect(() => {
        if (!register) return undefined;
        return register(() => (typeof latest.current === 'function' ? latest.current() : undefined));
    }, [register]);
};

/**
 * For screens whose data loads inside a useEffect rather than a named function.
 * Returns a counter that increments on every header refresh; add it to that
 * effect's dependency list and React re-runs the screen's own fetch, with its
 * own current filters — nothing is duplicated.
 *
 *   const refreshKey = usePageRefreshKey();
 *   useEffect(() => { dispatch(getClosing({ societyId, year })); },
 *             [dispatch, societyId, year, refreshKey]);
 *
 * Only attach it to the effects that load the screen's data — not enum or
 * dropdown loads, which is exactly the extra work refresh is meant to avoid.
 */
export const usePageRefreshKey = () => {
    const [key, setKey] = useState(0);
    usePageRefresh(() => setKey(k => k + 1));
    return key;
};

/** The header button. Renders nothing until a screen has registered. */
export const PageRefreshButton = () => {
    const { available, refreshing, refresh } = useContext(StateContext);
    if (!available) return null;
    return (
        <button
            type='button'
            onClick={refresh}
            disabled={refreshing}
            title='Refresh this page'
            aria-label='Refresh this page'
            className='w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center border-0'
            style={{ cursor: refreshing ? 'wait' : 'pointer' }}
        >
            <Icon
                icon='mdi:refresh'
                className='text-primary-light text-xl'
                style={refreshing ? { animation: 'page-refresh-spin 0.8s linear infinite' } : undefined}
            />
            <style>{'@keyframes page-refresh-spin { to { transform: rotate(360deg); } }'}</style>
        </button>
    );
};
